import { Dispatch, SetStateAction, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useFragment } from '../../../apollo/__generated__';
import { useFetchMore } from '../../../utils/hooks.ts';
import Table from '../../../components/table/table.tsx';
import { uniqBy } from 'lodash';
import Search from '../../../components/search.tsx';
import {
  UserActivityLogFilterInput,
  UserActivityLogType,
  UserRole,
} from '../../../apollo/__generated__/graphql.ts';
import {
  GET_USER_ACTIVITY_LOGS,
  USER_ACTIVITY_LOG_TABLE_FRAGMENT,
} from '../../../apollo/user-activity-log.ts';
import { userActivityLogColumns } from '../../../constants/columns/user-activity-log.ts';
import UserActivityLogRowChildrensInfo from './user-activity-log-row-info.tsx';
import { usePayloadSearch } from '../../../hooks/use-payload-search/index.ts';
import { renamedLogTypes } from '../../../constants/renamed-log-types.ts';

interface IProps {
  defaultFilter?: Partial<Record<UserActivityLogFilterKey, string>>;
}

type UserActivityLogFilterKey =
  | 'email'
  | 'logDescription'
  | 'name'
  | 'role'
  | 'schoolName'
  | 'type';

type FilterSlotId = 'primary' | 'secondary';

type FilterSlot = {
  key: UserActivityLogFilterKey;
  value?: string;
};

type FilterSlotsState = Record<FilterSlotId, FilterSlot>;

const initialFilter: Partial<UserActivityLogFilterInput> = {
  email: undefined,
  logDescription: undefined,
  name: undefined,
  role: undefined,
  schoolName: undefined,
  type: undefined,
};

const filterSearchOptions = [
  { key: 'name', title: 'User Name' },
  { key: 'email', title: 'Email' },
  { key: 'schoolName', title: 'School' },
  { key: 'role', title: 'Role' },
  { key: 'type', title: 'Activity Type' },
  { key: 'logDescription', title: 'Log Description' },
];

const secondaryFilterSearchOptions = [
  { key: 'type', title: 'Activity Type' },
  { key: 'logDescription', title: 'Log Description' },
  { key: 'name', title: 'User Name' },
  { key: 'email', title: 'Email' },
  { key: 'schoolName', title: 'School' },
  { key: 'role', title: 'Role' },
] ;

const getFilterValue = (
  key: UserActivityLogFilterKey,
  value?: string,
): UserActivityLogFilterInput[UserActivityLogFilterKey] => {
  if (!value) return undefined;

  if (key === 'role') return value as UserRole;
  if (key === 'type') return value as UserActivityLogType;

  return value;
};

const getInitialFilterSlots = (
  defaultFilter?: Partial<Record<UserActivityLogFilterKey, string>>,
): FilterSlotsState => {
  const presetEntries = Object.entries(defaultFilter || {}).filter(
    (entry): entry is [UserActivityLogFilterKey, string] =>
      entry[1] !== undefined,
  );

  return {
    primary: {
      key: presetEntries[0]?.[0] || 'name',
      value: presetEntries[0]?.[1],
    },
    secondary: {
      key: presetEntries[1]?.[0] || 'type',
      value: presetEntries[1]?.[1],
    },
  };
};

const buildFilterFromSlots = (filterSlots: FilterSlotsState) => {
  return Object.values(filterSlots).reduce(
    (acc, slot) => {
      if (!slot.value) return acc;

      return {
        ...acc,
        [slot.key]: getFilterValue(slot.key, slot.value),
      };
    },
    { ...initialFilter },
  );
};

const UserActivityLogsBrowser = ({ defaultFilter }: IProps) => {
  const FETCH_LIMIT = 20;

  const initPaging = {
    page: 1,
    limit: FETCH_LIMIT,
  };

  const initialFilterSlots = getInitialFilterSlots(defaultFilter);
  const filterSlotsRef = useRef<FilterSlotsState>(initialFilterSlots);

  const { payload, updateFilter, increasePage } =
    usePayloadSearch<UserActivityLogFilterInput>({
      initFilter: buildFilterFromSlots(initialFilterSlots),
      initPaging,
    });

  const { data, fetchMore, loading } = useQuery(GET_USER_ACTIVITY_LOGS, {
    fetchPolicy: 'network-only',
    variables: {
      paging: { ...initPaging },
      filter: payload.filter,
    },
  });

  const userActivityLogs = useFragment(
    USER_ACTIVITY_LOG_TABLE_FRAGMENT,
    data?.userActivityLog.getUserActivityLogs.results,
  );

  const onFetch = (setFetchMore: Dispatch<SetStateAction<boolean>>) => {
    const total = Number(data?.userActivityLog.getUserActivityLogs.total);

    if (total && Number(userActivityLogs?.length) < total) {
      setFetchMore(true);

      return fetchMore({
        variables: {
          paging: {
            page: payload.paging.page + 1,
            limit: FETCH_LIMIT,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!data?.userActivityLog.getUserActivityLogs.results) return prev;

          const union = [
            ...(prev.userActivityLog?.getUserActivityLogs?.results || []),
            ...fetchMoreResult.userActivityLog.getUserActivityLogs.results,
          ];
          const unique = uniqBy(union, 'id');

          if (unique.length !== union.length) {
            console.error('duplicates on merge detected');
          }

          return {
            userActivityLog: {
              ...prev.userActivityLog,
              getUserActivityLogs: {
                ...fetchMoreResult.userActivityLog.getUserActivityLogs,
                results: unique,
              },
            },
          };
        },
      }).then(() => {
        increasePage();
      });
    }
  };

  const { ref, isFetchMore } = useFetchMore(onFetch);
  const isLoading = isFetchMore || loading;

  const onFilterChange = (slotId: FilterSlotId) => {
    return (value?: string, key?: string) => {
      if (!key) return;

      const filterKey = key as UserActivityLogFilterKey;
      const nextFilterSlots = {
        ...filterSlotsRef.current,
        [slotId]: {
          key: filterKey,
          value,
        },
      };

      filterSlotsRef.current = nextFilterSlots;
      updateFilter(buildFilterFromSlots(nextFilterSlots));
    };
  };

  return (
    <div>
      <section className={'flex items-center justify-between'}>
        <div>
          <h1>{'User activity log Browser'}</h1>
          <p className={'description'}>A list of user activity logs</p>
        </div>
      </section>

      <div className={'mt-4 flex flex-wrap gap-3'}>
        <Search
          key={'PrimarySearch'}
          removeXicon={true}
          onChangeFilter={onFilterChange('primary')}
          searchBy={filterSearchOptions}
          optionsData={{
            role: Object.values(UserRole).map((role) => ({
              key: role,
              title: role,
            })),
          }}
        />
        <Search
          key={'SecondarySearch'}
          removeXicon={true}
          onChangeFilter={onFilterChange('secondary')}
          searchBy={secondaryFilterSearchOptions}
          optionsData={{
            type: Object.values(UserActivityLogType)
              .filter((type) => type !== UserActivityLogType.ExportXlsx)
              .map((type) => ({
                key: type,
                title: renamedLogTypes[type] || type,
              })),
          }}
        />
      </div>

      <hr className={'my-2 opacity-10'} />

      <Table
        selected={[]}
        onSelectAmount={() => {}}
        onResetAll={() => {}}
        isLoading={isLoading}
        columns={userActivityLogColumns}
        sort={{}}
        sortKeys={[]}
        setSort={() => {}}
        isEmpty={!userActivityLogs?.length}
      >
        {userActivityLogs?.map((log, index, array) => {
          return (
            <tr key={log.id} ref={index === array.length - 5 ? ref : null}>
              <UserActivityLogRowChildrensInfo {...log} />
            </tr>
          );
        })}
      </Table>
    </div>
  );
};
export default UserActivityLogsBrowser;
